import Button from 'common/Button/Button';
import { useEffect, useId, useState } from 'react';
import EphanDataService from 'services/EphanDataService';
import './SearchBar.css';
import { useLanguage } from 'hooks/useLanguage';

/**
 * Пошук і фільтри реєстру — ADR-0005, ADR-0008, ADR-0015.
 *
 * Усі фільтри — параметри запиту до джерела, а не відбір уже завантаженої
 * сторінки: інакше «клас E» шукався б лише серед двох десятків карток, які
 * встигли приїхати. Фільтр за регіоном вилучено — джерело більше не зберігає
 * регіонів узагалі.
 */
export default function SearchBar({
  catalogs,
  danceClasses,
  judgeCategories,
  loading,
  total,
  loadedCount,
  onSearch,
  onReset,
  isSearching,
}) {
  const language = useLanguage();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [clubs, setClubs] = useState([]);
  const searchId = useId();

  const needsClubs = catalogs === 'dancers' || catalogs === 'coaches';
  const hasFilters = needsClubs || catalogs === 'judges';

  // Перелік клубів потрібен лише як варіанти фільтра, тому береться одним
  // запитом і не бере участі в показі карток.
  useEffect(() => {
    if (!needsClubs) {
      setClubs([]);
      return undefined;
    }

    let cancelled = false;

    EphanDataService.getClubs({ limit: 100, sort: 'name', order: 'asc' }).then(({ result }) => {
      if (!cancelled) setClubs(result);
    });

    return () => {
      cancelled = true;
    };
  }, [needsClubs]);

  function handleFormSubmit(event) {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const { name, club, danceClass, category } = event.target;
    const filters = {};

    if (name?.value?.trim()) filters.search = name.value.trim();
    if (club?.value && club.value !== 'choose') filters.clubId = club.value;
    if (danceClass?.value && danceClass.value !== 'choose') filters.danceClass = danceClass.value;
    if (category?.value && category.value !== 'choose') filters.judgeCategory = category.value;

    onSearch(filters);
  }

  function handleResetFilters() {
    const form = document.getElementById('search');

    if (form) {
      const { name, club, danceClass, category } = form;
      if (name) name.value = '';
      [club, danceClass, category].forEach((field) => {
        if (field) field.value = 'choose';
      });
    }

    onReset();
  }

  const searchPlaceholder = {
    dancers: language === 'en' ? 'Name or surname of dancer' : "Ім'я або прізвище танцюриста",
    clubs: language === 'en' ? 'Name of club' : 'Назва клубу',
    coaches: language === 'en' ? 'Name or surname of coach' : "Ім'я або прізвище тренера",
    judges: language === 'en' ? 'Name or surname of judge' : "Ім'я або прізвище судді",
  }[catalogs];

  return (
    <div className={`search-form ${catalogs}`}>
      <form className='form' id='search' onSubmit={handleFormSubmit}>
        <div className='search-row'>
          <div className='form-control search-field'>
            <label className='label' htmlFor={searchId}>
              {language === 'en' ? 'Search' : 'Пошук'}
            </label>
            <input className='input search-input' id={searchId} placeholder={searchPlaceholder} name='name' />
          </div>

          <div className='search-actions'>
            <Button variant='primary' className='search-btn' type='submit'>
              {language === 'en' ? 'Find' : 'Знайти'}
            </Button>

            {hasFilters && (
              <Button
                variant='secondary'
                className={`filters-toggle ${filtersOpen ? 'is-open' : ''}`}
                aria-expanded={filtersOpen}
                onClick={() => setFiltersOpen((open) => !open)}
              >
                {language === 'en' ? 'Filters' : 'Фільтри'}
                <svg width='18' height='18' viewBox='0 0 24 24' aria-hidden='true'>
                  <path
                    d='M6 9l6 6 6-6'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1.6'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </Button>
            )}
          </div>
        </div>

        <div className={`form-selectors-shell ${filtersOpen ? 'is-open' : ''}`}>
          <div className='form-selectors-wrapper'>
            {needsClubs && clubs.length > 0 && (
              <select
                name='club'
                className='select search-select'
                defaultValue='choose'
                aria-label={language === 'en' ? 'Club' : 'Клуб'}
              >
                <option value='choose'>{language === 'en' ? 'Any club' : 'Будь-який клуб'}</option>
                {clubs.map((club) => (
                  <option value={club.id} key={club.id}>
                    {(club.name || '').trim()}
                  </option>
                ))}
              </select>
            )}

            {catalogs === 'dancers' && (
              <select
                name='danceClass'
                className='select search-select'
                defaultValue='choose'
                aria-label={language === 'en' ? 'Class' : 'Клас'}
              >
                <option value='choose'>{language === 'en' ? 'Any class' : 'Будь-який клас'}</option>
                {danceClasses.map((danceClass) => (
                  <option value={danceClass.value} key={danceClass.value}>
                    {language === 'en' ? danceClass.label : danceClass.labelUa}
                  </option>
                ))}
              </select>
            )}

            {catalogs === 'judges' && (
              <select
                name='category'
                className='select search-select'
                defaultValue='choose'
                aria-label={language === 'en' ? 'Category' : 'Категорія'}
              >
                <option value='choose'>{language === 'en' ? 'Any category' : 'Будь-яка категорія'}</option>
                {judgeCategories.map((category) => (
                  <option value={category.value} key={category.value}>
                    {language === 'en' ? category.label : category.labelUa}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </form>

      {!loading && isSearching && (
        <div className='search-summary'>
          <span className='tnum'>
            {loadedCount} {language === 'en' ? 'of' : 'з'} {total}
          </span>
          <Button variant='link' className='reset-filters-btn' onClick={handleResetFilters}>
            {language === 'en' ? 'Reset filters' : 'Скинути фільтри'}
          </Button>
        </div>
      )}
    </div>
  );
}
